import React from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: "400px",
    display: "flex",
    flexDirection: "column",
  },
  chartWrapper: {
    flex: 1,
  },
  toolbar: {
    marginBottom: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
  },
}));

interface TimeSeries {
  period: string;
  ticketsCount: number;
  messagesCount: number;
}

interface TimeSeriesChartProps {
  data: TimeSeries[];
  groupBy?: "day" | "week" | "month";
}

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
  data,
  groupBy = "day",
}) => {
  const classes = useStyles();
  const [chartType, setChartType] = React.useState<"line" | "bar">("line");

  const handleChartTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newType: "line" | "bar" | null
  ) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  const getXAxisLabel = () => {
    switch (groupBy) {
      case "week":
        return "Semana";
      case "month":
        return "Mês";
      default:
        return "Data";
    }
  };

  const formatXAxis = (value: string) => {
    if (groupBy === "day") {
      // Formato: "2026-03-25" -> "25/03"
      const [year, month, day] = value.split("-");
      return `${day}/${month}`;
    } else if (groupBy === "week") {
      // Formato: "2026-W13" -> "S13"
      return value.replace("-W", "S");
    } else {
      // Formato: "2026-03" -> "Mar/2026"
      const [year, month] = value.split("-");
      const monthName = new Date(parseInt(year), parseInt(month) - 1, 1)
        .toLocaleString("pt-BR", { month: "short" })
        .toUpperCase();
      return `${monthName}/${year}`;
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.toolbar}>
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          size="small"
        >
          <ToggleButton value="line">📈 Linha</ToggleButton>
          <ToggleButton value="bar">📊 Barras</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box className={classes.chartWrapper}>
        {chartType === "line" ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="period"
                tickFormatter={formatXAxis}
                tick={{ fontSize: 12 }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#f9f9f9",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
                formatter={(value) => {
                  if (typeof value === "number") return value.toLocaleString("pt-BR");
                  return value;
                }}
              />
              <Legend wrapperStyle={{ paddingTop: "20px" }} />
              <Line
                type="monotone"
                dataKey="ticketsCount"
                name="Tickets"
                stroke="#4CAF50"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="messagesCount"
                name="Mensagens"
                stroke="#2196F3"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="period"
                tickFormatter={formatXAxis}
                tick={{ fontSize: 12 }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#f9f9f9",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
                formatter={(value) => {
                  if (typeof value === "number") return value.toLocaleString("pt-BR");
                  return value;
                }}
              />
              <Legend wrapperStyle={{ paddingTop: "20px" }} />
              <Bar dataKey="ticketsCount" name="Tickets" fill="#4CAF50" />
              <Bar dataKey="messagesCount" name="Mensagens" fill="#2196F3" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default TimeSeriesChart;
